import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Swords, Trophy, Target, Sparkles, ArrowRight, Play } from "lucide-react";
import { api } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";

function StatCard({ label, value, testId }) {
  return (
    <Card className="border-border" data-testid={testId}>
      <CardContent className="p-6">
        <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-medium">{label}</div>
        <div className="mt-2 text-3xl font-display font-extrabold tracking-tight">{value}</div>
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [myGames, setMyGames] = useState([]);
  const [lb, setLb] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, g, l] = await Promise.all([
          api.get("/stats/me"),
          api.get("/games", { params: { mine: true, limit: 5 } }),
          api.get("/leaderboards", { params: { period: "week" } }),
        ]);
        setStats(s.data); setMyGames(g.data); setLb(l.data.slice(0, 5));
      } catch (_) {}
    })();
  }, []);

  const nextXp = stats?.level ? 100 * stats.level : 100;
  const progressPct = stats ? Math.min(100, (stats.xp % nextXp) / nextXp * 100) : 0;

  return (
    <AppLayout>
      <div className="mb-8 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="text-sm uppercase tracking-[0.2em] text-primary font-semibold">
            {t("dashboard.hello")}
          </div>
          <h1 className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl mt-1">
            {user?.name}
          </h1>
          <p className="mt-1 text-muted-foreground font-editorial italic">
            L'émergence commence par un premier pas.
          </p>
        </div>
        <div className="flex gap-3">
          <Button size="lg" onClick={() => navigate("/play")} data-testid="dashboard-new-game-btn">
            <Play className="w-4 h-4 mr-2" /> {t("dashboard.actions.play")}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard label={t("dashboard.stats.games")} value={stats?.games_played ?? 0} testId="stat-games" />
        <StatCard label={t("dashboard.stats.wins")} value={stats?.wins ?? 0} testId="stat-wins" />
        <StatCard label={t("dashboard.stats.best")} value={stats?.best_score ?? 0} testId="stat-best" />
        <StatCard label={t("dashboard.stats.avg")} value={stats?.avg_score ?? 0} testId="stat-avg" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Progress */}
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-display">
              <Sparkles className="w-5 h-5 text-primary" /> {t("dashboard.progress")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-muted-foreground">{t("dashboard.level")}</div>
            <div className="text-4xl font-display font-black">{stats?.level ?? user?.level ?? 1}</div>
            <Progress value={progressPct} className="mt-4" />
            <div className="mt-2 text-xs text-muted-foreground">
              {stats?.xp ?? 0} / {(stats?.level ?? 1) * 100} XP
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <Badge variant="secondary">{t("stats.winRate")}: {stats?.win_rate ?? 0}%</Badge>
            </div>
          </CardContent>
        </Card>

        {/* Recent games */}
        <Card className="border-border lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2 font-display">
              <Swords className="w-5 h-5 text-primary" /> {t("dashboard.recent")}
            </CardTitle>
            <Link to="/history" className="text-xs text-primary hover:underline">
              {t("nav.history")} <ArrowRight className="w-3 h-3 inline" />
            </Link>
          </CardHeader>
          <CardContent>
            {myGames.length === 0 ? (
              <div className="text-sm text-muted-foreground py-8 text-center">{t("dashboard.noGames")}</div>
            ) : (
              <div className="divide-y divide-border">
                {myGames.map((g) => {
                  const you = g.players.find((p) => p.user_id === user.user_id);
                  const opp = g.players.find((p) => p.user_id !== user.user_id);
                  const result = g.status === "active" ? "•" :
                    g.winner_id === user.user_id ? t("history.won") :
                    g.winner_id ? t("history.lost") : t("history.draw");
                  return (
                    <button
                      key={g.game_id}
                      className="w-full py-3 flex items-center gap-3 hover:bg-muted/40 px-2 -mx-2 rounded-lg text-left"
                      onClick={() => navigate(`/game/${g.game_id}`)}
                      data-testid={`dashboard-recent-${g.game_id}`}
                    >
                      <Avatar className="h-9 w-9"><AvatarImage src={opp?.picture} /><AvatarFallback>{(opp?.name || "?").slice(0,2).toUpperCase()}</AvatarFallback></Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{opp?.name || "—"}</div>
                        <div className="text-xs text-muted-foreground">
                          {you?.score ?? 0} · {opp?.score ?? 0} · {g.status}
                        </div>
                      </div>
                      <Badge variant={g.winner_id === user.user_id ? "default" : "secondary"}>{result}</Badge>
                    </button>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Leaderboard */}
        <Card className="border-border lg:col-span-3">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2 font-display">
              <Trophy className="w-5 h-5 text-primary" /> {t("dashboard.leaderboard")}
            </CardTitle>
            <Link to="/leaderboards" className="text-xs text-primary hover:underline">
              {t("nav.leaderboards")} <ArrowRight className="w-3 h-3 inline" />
            </Link>
          </CardHeader>
          <CardContent>
            {lb.length === 0 ? (
              <div className="text-sm text-muted-foreground py-4">—</div>
            ) : (
              <div className="grid gap-1">
                {lb.map((row, i) => (
                  <div key={row.user_id} className="flex items-center gap-3 py-2" data-testid={`lb-preview-${i}`}>
                    <div className="w-8 text-center font-display font-bold text-lg">{i + 1}</div>
                    <Avatar className="h-8 w-8"><AvatarImage src={row.picture} /><AvatarFallback>{(row.name || "?").slice(0,2).toUpperCase()}</AvatarFallback></Avatar>
                    <div className="flex-1 text-sm">{row.name}</div>
                    <div className="text-sm font-medium tabular-nums">{row.total_score}</div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
